import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import Content from '../layout/content/Content'
import Head from '../layout/head/Head'
import { useTranslation } from 'react-i18next'
import {
  ImporterMapGraphSection,
  ImporterTableSection,
  PotentialMapGraphSection,
  PotentialTableSection
} from './mc-components/McPageComponents'
import FirmDetailsModal from '../components/firm-card/FirmDetailsModal'
import McPageHelpers from './mc-components/McPageHelpers'
import {
  useDSimporterGraphMapSectionExplanation,
  useDSpotentialGraphMapSectionDataExplanation,
  useDStotalImporterCompanyCount,
  useDStotalPotentialCompanyCount,
  useDSrawPotentialMap,
  useDSrawImporterMap,
  useDSselectedTableName,
  useDStradeRegistryTables,
  useDataStoreActions,
  useDStradeRegistryDropdownSelection
} from '../store/dataStore'
import McPageHeader from '../components/page-header/McPageHeader'
import McPdfExport from './mc-components/McPdfExport'
import { useSDSsearchParams } from '../store/searchDataStore'
import { usePDFselectedComponentIds } from '../store/pdfStore'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import classnames from 'classnames'
import { MarketCalculusCard } from '../components/preview/Preview'
import TradeRegistryTabs
  from './mc-components/importer-companies/TradeRegistryTabs'
import ExporterTableSection
  from './mc-components/importer-companies/ExporterTableSection'
import { useRAWtradeRegistry } from '../store/rawStore'
import { Tooltip } from '@mui/material'
import { useGlobalStoreData } from '../store/globalStore'

const ImporterCompanies = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const searchParams = useSDSsearchParams()
  const selectedComponentIds = usePDFselectedComponentIds()

  const importerGraphMapSectionExplanation = useDSimporterGraphMapSectionExplanation()
  const potentialGraphMapSectionDataExplanation = useDSpotentialGraphMapSectionDataExplanation()
  const totalImporterCompanyCount = useDStotalImporterCompanyCount()
  const totalPotentialCompanyCount = useDStotalPotentialCompanyCount()
  const rawPotentialMap = useDSrawPotentialMap()
  const rawImporterMap = useDSrawImporterMap()
  const selectedTableName = useDSselectedTableName()
  const tradeRegistryTables = useDStradeRegistryTables()
  const tradeRegistryDropdownSelection = useDStradeRegistryDropdownSelection()

  const {
    setTradeRegistryTables,
    setTradeRegistryDropdownSelection,
    setTradeRegistryFilters
  } = useDataStoreActions()
  const languageGSD = useGlobalStoreData.language()

  const header = `${t(
    'graph_importer_analysis')} (${totalImporterCompanyCount} ${t('piece')})`
  const headerPotential = `${t(
    'graph_potential_analysis')} (${totalPotentialCompanyCount} ${t('piece')})`
  const importerCountryName = McPageHelpers.findCountryName(
    searchParams.countryToExport)
  const subTitle = `${importerCountryName} ${searchParams.hsCode}`
  const subPotTitle = `${t('world')} ${searchParams.hsCode}`
  const searchHeader = `${searchParams.hsCode} - ${importerCountryName} - ${McPageHelpers.findCountryName(
    searchParams.yourCountry)}`

  useEffect(() => {
    const params = new URL(window.location.href).searchParams
    if (params.has('scroll')) {
      McPageHelpers.scrollToElement(params.get('scroll'))
    }
  }, [])

  const importerTableRef = useRef(null)
  const potentialTableRef = useRef(null)

  const tradeRegistry = useRAWtradeRegistry()

  const allTabs = useMemo(() => {
    const tabs = [
      { id: 1, key: 'wholesalers', label: 'wholesalers', group: 'tradeRegistry' },
      { id: 2, key: 'retailers', label: 'retailers', group: 'tradeRegistry' },
      { id: 3, key: 'suppliers', label: 'suppliers', group: 'tradeRegistry' },
      { id: 4, key: 'service_providers', label: 'service_providers', group: 'tradeRegistry' },
      { id: 5, key: 'spFirms', label: 'spFirms', group: 'matching' },
      { id: 6, key: 'rfFirms', label: 'rfFirms', group: 'matching' },
      { id: 8, key: 'p7Exporters', label: 'p7Exporters', group: 'matching' },
      { id: 7, key: 'fairParticipants', label: 'fairParticipants', group: 'other', disabled: true }
    ]

    return tabs.filter(tab => {
      if (['wholesalers', 'retailers', 'suppliers', 'service_providers'].includes(tab.key)) {
        return tradeRegistry?.[tab.key]
      }
      return true
    })
  }, [tradeRegistry])

  const defaultTab = useMemo(() => {
    const tradeRegistryTab = allTabs.find(tab => ['wholesalers', 'retailers', 'suppliers', 'service_providers'].includes(tab.key))
    return tradeRegistryTab ? tradeRegistryTab.id : 5
  }, [allTabs])

  const [activeTab, setActiveTab] = useState(defaultTab)

  const toggleAltTab = altTab => activeTab !== altTab && setActiveTab(altTab)

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  const columnsForPdf = [
    {
      name: (<div style={{ textAlign: 'center' }}>{t(
        'mailTemplate.wizard.firmList.firmName')}</div>),
      selector: row => row.companyName,
      wrap: true,
      left: true,
      width: '20%'
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t(
        'mailTemplate.wizard.firmList.website')}</div>),
      selector: row => row.website,
      wrap: true,
      left: true,
      width: '15%',
      cell: row => (row.website)
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t(
        'mailTemplate.wizard.firmList.email')}</div>),
      selector: row => row.emailList?.[0],
      wrap: true,
      left: true,
      width: '20%',
      cell: row => (
        <>
          {row.emailList?.length > 1
            ? (
              <Tooltip
                title={
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {row.emailList.slice(1).join('\n')}
                  </div>
                }
                arrow
              >
                <span>{row.emailList?.[0]}...</span>
              </Tooltip>
              )
            : row.emailList?.[0]}
        </>
      )
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t(
        'mailTemplate.wizard.firmList.sector')}</div>),
      selector: row => row.companySectors?.map(
        sector => `-${sector.code}-${sector?.name?.[languageGSD] || ''}`).join(', '),
      wrap: true,
      left: true,
      width: '25%',
      cell: row => (
        <Tooltip
          title={
            <div style={{ whiteSpace: 'pre-line' }}>
              {row.companySectors.map(
                sector => `-${sector.code}-${sector?.name?.[languageGSD] ||
                ''}`).join('\n')}
            </div>
          }
          arrow
        >
          <span>{`${row.companySectors?.[0]?.code}${row.companySectors?.length >
          1
            ? '...'
            : ''}`}</span>
        </Tooltip>
      )
    }
  ]

  const pdfDownload = (onlyContent = false) => {
    const importerTablePdfContent = importerTableRef.current.generateImporterTablePdf(
      'importerTablePdfContent')
    const potentialTablePdfContent = potentialTableRef.current.generateImporterTablePdf(
      'potentialTablePdfContent')

    const tradeRegistryTabs = allTabs.filter(tab => ['wholesalers', 'retailers', 'suppliers', 'service_providers'].includes(tab.key))

    const tradeRegistryTablesPdfContent = tradeRegistryTabs.map(tab => {
      const tableId = tab.key
      if (selectedComponentIds.includes(tableId)) {
        const result = {
          [tableId]: [
            McPageHelpers.generateDataTableContentForPdf(columnsForPdf,
              tradeRegistryTables[tableId], true, 'after')
          ]
        }
        return result
      } else return null
    })

    const filterImporterMap = rawImporterMap?.filter(item => item.formatValue)
    const filterPotentialMap = rawPotentialMap?.filter(
      item => item.formatValue || item.avgPrice)

    return McPdfExport.ImporterCompanies(
      onlyContent,
      searchHeader,
      filterImporterMap,
      filterPotentialMap,
      importerTablePdfContent,
      potentialTablePdfContent,
      tradeRegistryTablesPdfContent,
      subTitle,
      subPotTitle,
      header,
      headerPotential,
      selectedComponentIds,
      [
        importerGraphMapSectionExplanation,
        potentialGraphMapSectionDataExplanation]
    )
  }

  useImperativeHandle(ref, () => ({
    async getPdfContent () {
      return pdfDownload(true)
    }
  }))

  return (
    <>
      <Head title={t('menu.importer_companies')}/>
      <Content>
        <McPageHeader title={t('menu.importer_companies')}/>
        <MarketCalculusCard className="mb-2">
          <Nav tabs className="nav-tabs-s2" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {allTabs.map(({ id, label, disabled, group }, index) => {
              const prevGroup = index > 0 ? allTabs[index - 1].group : null
              const showSeparator = prevGroup && prevGroup !== group

              return (
                <React.Fragment key={id}>
                  {showSeparator && (
                    <div style={{
                      width: '3px',
                      backgroundColor: '#8094ae',
                      margin: '0 12px',
                      alignSelf: 'stretch'
                    }}/>
                  )}
                  <NavItem>
                    <NavLink
                      href="#tab"
                      className={classnames('btn btn-sm',
                        { 'btn-primary': activeTab === id },
                        { disabled })}
                      onClick={ev => {
                        ev.preventDefault()
                        if (!disabled) {
                          toggleAltTab(id)
                        }
                      }}
                      style={{
                        ...(disabled ? { cursor: 'not-allowed', opacity: 0.5 } : {}),
                        padding: '8px 16px'
                      }}
                    >
                      <Tooltip
                        title={t(`mailTemplate.wizard.firmList.tables.${label}`)}
                        placement="top">
                        {t(`mailTemplate.wizard.firmList.tables.${label}`)}
                      </Tooltip>
                    </NavLink>
                  </NavItem>
                </React.Fragment>
              )
            })}
          </Nav>
        </MarketCalculusCard>
        <TabContent activeTab={activeTab}>
          {allTabs.map((tab) => {
            if (tab.key === 'spFirms') {
              return (
                <TabPane key={tab.id} tabId={tab.id} data-tab-id={'sp-companies'}>
                  <ImporterTableSection ref={importerTableRef}/>
                  <ImporterMapGraphSection/>
                </TabPane>
              )
            } else if (tab.key === 'rfFirms') {
              return (
                <TabPane key={tab.id} tabId={tab.id} data-tab-id={'rf-companies'}>
                  <PotentialTableSection ref={potentialTableRef}/>
                  <PotentialMapGraphSection/>
                </TabPane>
              )
            } else if (tab.key === 'p7Exporters') {
              return (
                <TabPane key={tab.id} tabId={tab.id} data-tab-id={'p7-exporters'}>
                  <ExporterTableSection isActive={activeTab === tab.id}/>
                </TabPane>
              )
            } else if (tab.key === 'fairParticipants') {
              return (
                <TabPane key={tab.id} tabId={tab.id} data-tab-id={'fair-participants'}>
                  <MarketCalculusCard>
                    <div className="text-center p-4">
                      <h5>{t('coming_soon')}</h5>
                    </div>
                  </MarketCalculusCard>
                </TabPane>
              )
            } else {
              return (
                <TabPane key={tab.id} tabId={tab.id} data-tab-id={tab.key}>
                  <TradeRegistryTabs id={tab.key} dropdownData={tradeRegistry[tab.key]}
                                     tableData={tradeRegistryTables[tab.key] ?? []}
                                     setTableData={setTradeRegistryTables}
                                     selectedOptions={tradeRegistryDropdownSelection[tab.key] ??
                                       []}
                                     setSelectedOptions={setTradeRegistryDropdownSelection}
                                     setFilters={setTradeRegistryFilters}
                                     isActive={activeTab === tab.id}/>
                </TabPane>
              )
            }
          })}
        </TabContent>
        <FirmDetailsModal pageName={selectedTableName}/>
      </Content>
    </>
  )
})

export default ImporterCompanies
